import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { LEARNING_KEY } from '../api';

/**
 * חידונים (PRD 5.6.3).
 *
 * כל הבדיקה בשרת: get_quiz מחזיר את השאלות בלי לסמן מה נכון,
 * submit_answer הוא זה שקובע, ו finish_quiz מחשב את הציון. אין כאן
 * שום לוגיקת ניקוד בצד הלקוח, כי כל מה שיורד לדפדפן אפשר לקרוא.
 */

export type QuizOption = { id: string; text: string };

export type QuizQuestion = {
  id: string;
  prompt: string;
  image_path: string | null;
  options: QuizOption[] | null;
};

export type Quiz = {
  id: string;
  title: string;
  time_limit_seconds: number | null;
  questions: QuizQuestion[];
};

export type AnswerResult = {
  correct: boolean;
  explanation: string | null;
  correct_option_id: string | null;
};

export type QuizResult = { score: number; correct: number; total: number; passed: boolean };

export function useQuiz(quizId: string) {
  return useQuery({
    queryKey: ['quiz', quizId],
    enabled: Boolean(quizId),
    // השאלות מעורבבות בשרת כש shuffle דלוק. refetch באמצע חידון היה
    // מחליף את הסדר מתחת לאצבע, ולכן הן נטענות פעם אחת.
    staleTime: Infinity,
    gcTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<Quiz | null> => {
      const { data, error } = await supabase.rpc('get_quiz', { p_quiz: quizId });
      if (error) throw error;
      return data as Quiz | null;
    },
  });
}

export function useQuizRun(quizId: string) {
  const queryClient = useQueryClient();

  const start = useMutation({
    mutationFn: async (): Promise<string> => {
      const { data, error } = await supabase.rpc('start_quiz', { p_quiz: quizId });
      if (error) throw error;
      return data as string;
    },
  });

  const answer = useMutation({
    mutationFn: async (input: {
      attemptId: string;
      questionId: string;
      optionId: string;
      ms: number;
    }): Promise<AnswerResult> => {
      const { data, error } = await supabase.rpc('submit_answer', {
        p_attempt: input.attemptId,
        p_question: input.questionId,
        p_option: input.optionId,
        p_ms: input.ms,
      });
      if (error) throw error;
      return data as AnswerResult;
    },
  });

  const finish = useMutation({
    mutationFn: async (attemptId: string): Promise<QuizResult> => {
      const { data, error } = await supabase.rpc('finish_quiz', { p_attempt: attemptId });
      if (error) throw error;
      return data as QuizResult;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEARNING_KEY });
      // החידון מזכה בנקודות, והמסך שלי מראה אותן
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return { start, answer, finish };
}
